import { useState } from "react";
import {
  FESTIVE_COMPANY_ICONS,
  FESTIVE_FESTIVAL_ICONS,
  FESTIVE_PRESETS,
  type FestiveEventAdminDto,
  type FestiveEventDto,
  type FestiveEventInput,
  type FestiveIconKey,
  type FestiveRecurrence,
} from "@hrms/types";

import { api, errMsg } from "../../lib/api.js";
import { toFestival } from "../../lib/festive/festivals.js";
import { FestiveBadge } from "../../components/ui/festive-badge.js";
import { FestiveLine } from "../../components/ui/festive-line.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Button } from "../../components/ui/button.js";
import { CheckboxList } from "../../components/ui/checkbox-list.js";
import { DateInput } from "../../components/ui/date-input.js";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { SelectField } from "../../components/ui/select-field.js";
import { Textarea } from "../../components/ui/textarea.js";

export interface OfficeOption { id: string; name: string; city: string }

const ICON_LABELS: Record<FestiveIconKey, string> = {
  diwali: "Diwali lamp", xmas: "Christmas tree", newyear: "Sparkles", holi: "Colour splash",
  eid: "Crescent moon", flag: "Flag", trophy: "Trophy", cake: "Cake", party: "Celebration",
  gift: "Gift", award: "Award", star: "Star", building: "Office building",
};

/** Swatches for the badge and the line. The light theme darkens each one automatically. */
const ACCENT_CHOICES = [
  { hex: "#FFC978", label: "Gold" }, { hex: "#FF9AA0", label: "Rose" },
  { hex: "#FFD166", label: "Yellow" }, { hex: "#FF9CC2", label: "Pink" },
  { hex: "#FFB36B", label: "Orange" }, { hex: "#8EC5FF", label: "Sky blue" },
  { hex: "#9AE6B4", label: "Mint" }, { hex: "#C4B5FD", label: "Lavender" },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

interface FormState {
  /** Built-in festival this event starts from. Empty for a custom event. Only sent on create. */
  presetKey: string;
  name: string;
  greeting: string;
  message: string;
  icon: FestiveIconKey;
  accent: string;
  recurrence: FestiveRecurrence;
  month: string;
  day: string;
  eventDate: string;
  windowDays: string;
  isActive: boolean;
  allOffices: boolean;
  offices: Set<string>;
}

const BLANK: FormState = {
  presetKey: "", name: "", greeting: "", message: "", icon: "star", accent: ACCENT_CHOICES[0]!.hex,
  recurrence: "ANNUAL", month: "1", day: "1", eventDate: "", windowDays: "7",
  isActive: true, allOffices: true, offices: new Set(),
};

function stateFrom(event: FestiveEventAdminDto | null): FormState {
  if (!event) return BLANK;
  return {
    presetKey: "",
    name: event.name,
    greeting: event.greeting,
    message: event.message,
    icon: event.icon,
    accent: event.accent,
    recurrence: event.recurrence,
    month: String(event.month ?? 1),
    day: String(event.day ?? 1),
    eventDate: event.eventDate ?? "",
    windowDays: String(event.windowDays),
    isActive: event.isActive,
    allOffices: event.allOffices,
    offices: new Set(event.offices.map((o) => o.id)),
  };
}

function fromPreset(key: string): Partial<FormState> {
  const p = FESTIVE_PRESETS.find((x) => x.presetKey === key);
  if (!p) return {};
  return {
    presetKey: p.presetKey, name: p.name, greeting: p.greeting, message: p.message, icon: p.icon,
    accent: p.accent, recurrence: p.recurrence, month: String(p.month ?? 1), day: String(p.day ?? 1),
  };
}

function toInput(f: FormState, presetKey: string | null): FestiveEventInput {
  const annual = f.recurrence === "ANNUAL";
  return {
    name: f.name,
    greeting: f.greeting,
    message: f.message,
    icon: f.icon,
    accent: f.accent,
    recurrence: f.recurrence,
    month: annual ? Number(f.month) : null,
    day: annual ? Number(f.day) : null,
    eventDate: annual ? null : f.eventDate || null,
    windowDays: Number(f.windowDays),
    isActive: f.isActive,
    allOffices: f.allOffices,
    offices: f.allOffices ? [] : [...f.offices],
    presetKey,
  };
}

/** The event as the line and the badge will draw it, for the live preview. */
function previewFestival(f: FormState) {
  const input = toInput(f, null);
  const dto: FestiveEventDto = {
    id: "preview",
    presetKey: f.presetKey || null,
    name: f.name || "Event name",
    greeting: f.greeting || "Happy event",
    message: f.message || "Your message appears here.",
    icon: f.icon,
    accent: f.accent,
    recurrence: f.recurrence,
    month: input.month ?? null,
    day: input.day ?? null,
    eventDate: input.eventDate ?? null,
    windowDays: Number(f.windowDays) || 7,
  };
  return toFestival(dto);
}

interface FestiveEventFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = a new event. */
  event: FestiveEventAdminDto | null;
  /** Built-in festivals not in the list yet, plus any already-present keys to hide. */
  existingPresetKeys: ReadonlySet<string>;
  offices: OfficeOption[];
  onSaved: () => void;
}

export function FestiveEventForm({ open, onOpenChange, event, existingPresetKeys, offices, onSaved }: FestiveEventFormProps) {
  const [form, setForm] = useState<FormState>(() => stateFrom(event));
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const addable = FESTIVE_PRESETS.filter((p) => !existingPresetKeys.has(p.presetKey));
  const preview = { festival: previewFestival(form), daysUntil: 5 };

  const save = async () => {
    setErr(null);
    try {
      if (event) await api.patch(`/admin/festive-events/${event.id}`, toInput(form, null));
      else await api.post("/admin/festive-events", toInput(form, form.presetKey || null));
      onSaved();
    } catch (e) {
      setErr(errMsg(e) ?? "Could not save the event.");
      throw e;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? "Edit event" : "Add event"}</DialogTitle>
          <DialogDescription>
            Shows on the login page and the dashboard before the date, then on the day itself.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          {!event && (
            <div className="md:col-span-2">
              <Label htmlFor="fe-start">Start from</Label>
              <SelectField id="fe-start" value={form.presetKey} onChange={(e) => setForm((f) => ({ ...f, ...fromPreset(e.target.value), presetKey: e.target.value }))}>
                <option value="">Custom event</option>
                {addable.map((p) => <option key={p.presetKey} value={p.presetKey}>{p.name} (built-in)</option>)}
              </SelectField>
            </div>
          )}

          <div>
            <Label htmlFor="fe-name">Event name</Label>
            <Input id="fe-name" value={form.name} maxLength={60} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Silver Jubilee" />
          </div>
          <div>
            <Label htmlFor="fe-greeting">Greeting</Label>
            <Input id="fe-greeting" value={form.greeting} maxLength={80} onChange={(e) => set("greeting", e.target.value)} placeholder="e.g. Happy Silver Jubilee" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="fe-message">Message</Label>
            <Textarea id="fe-message" rows={2} value={form.message} maxLength={240} onChange={(e) => set("message", e.target.value)} placeholder="One line shown on the dashboard" />
          </div>

          <div>
            <Label htmlFor="fe-icon">Icon</Label>
            {/* Flat options: SelectField reads direct <option> children, not <optgroup>. Festival icons first. */}
            <SelectField id="fe-icon" value={form.icon} onChange={(e) => set("icon", e.target.value as FestiveIconKey)}>
              {[...FESTIVE_FESTIVAL_ICONS, ...FESTIVE_COMPANY_ICONS].map((k) => (
                <option key={k} value={k}>{ICON_LABELS[k]}</option>
              ))}
            </SelectField>
          </div>
          <div>
            <Label htmlFor="fe-accent">Colour</Label>
            <SelectField id="fe-accent" value={form.accent} onChange={(e) => set("accent", e.target.value)}>
              {ACCENT_CHOICES.map((c) => <option key={c.hex} value={c.hex}>{c.label}</option>)}
            </SelectField>
          </div>

          <div>
            <Label htmlFor="fe-recurrence">When</Label>
            <SelectField id="fe-recurrence" value={form.recurrence} onChange={(e) => set("recurrence", e.target.value as FestiveRecurrence)}>
              <option value="ANNUAL">Every year on a date</option>
              <option value="ONE_TIME">One specific date</option>
            </SelectField>
          </div>
          {form.recurrence === "ANNUAL" ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="fe-month">Month</Label>
                <SelectField id="fe-month" value={form.month} onChange={(e) => set("month", e.target.value)}>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </SelectField>
              </div>
              <div>
                <Label htmlFor="fe-day">Day</Label>
                <SelectField id="fe-day" value={form.day} onChange={(e) => set("day", e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                </SelectField>
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="fe-date">Date</Label>
              <DateInput id="fe-date" value={form.eventDate} onChange={(iso) => set("eventDate", iso)} />
            </div>
          )}

          <div>
            <Label htmlFor="fe-window">Show from how many days before</Label>
            <Input id="fe-window" type="number" min={1} max={30} value={form.windowDays} onChange={(e) => set("windowDays", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="fe-status">Status</Label>
            <SelectField id="fe-status" value={form.isActive ? "active" : "paused"} onChange={(e) => set("isActive", e.target.value === "active")}>
              <option value="active">Active</option>
              <option value="paused">Paused (hidden everywhere)</option>
            </SelectField>
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="fe-scope">Who can see it</Label>
            <SelectField id="fe-scope" value={form.allOffices ? "all" : "some"} onChange={(e) => set("allOffices", e.target.value === "all")}>
              <option value="all">Every branch</option>
              <option value="some">Only the branches I choose</option>
            </SelectField>
            {!form.allOffices && (
              <div className="mt-2">
                <CheckboxList
                  options={offices.map((o) => ({ value: o.id, label: o.name, sub: o.city }))}
                  selected={form.offices}
                  onSelectedChange={(next) => set("offices", next)}
                  searchPlaceholder="Search branches"
                  emptyText="No branches yet"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 rounded-md border border-surface-200 p-4 md:col-span-2">
            <div className="relative h-14 w-14 shrink-0">
              <FestiveBadge active={preview} className="left-[15%] top-[15%] h-[70%] w-[70%]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs uppercase tracking-wide text-surface-400">Preview (shown as 5 days before)</p>
              <FestiveLine active={preview} className="mt-0" />
            </div>
          </div>
        </div>

        {err && <p role="alert" className="text-sm text-[var(--color-error-fg)]">{err}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <AsyncButton onClick={() => save()}>Save event</AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
